import React from 'react';
import classNames from 'classnames';
import { type WithTranslation } from '@wix/yoshi-flow-editor';
import withTranslate from '../../hoc/with-translate';
import { OneStar } from './rating-stars/one-star';
import { RatingStars } from './rating-stars/rating-stars';
import styles from './ratings-display.scss';

export const RatingsDisplayLayout = {
  default: 'default',
  bracket_separated: 'bracket-separated',
  with_range: 'with-range',
  count_only: 'count-only',
} as const;

export type RatingsDisplayLayoutValue =
  (typeof RatingsDisplayLayout)[keyof typeof RatingsDisplayLayout];

type Props = {
  className?: string;
  rating: number;
  count: number;
  isLoading?: boolean;
  useTransitions?: boolean;
  layout?: RatingsDisplayLayoutValue;
} & Pick<WithTranslation, 't'>;

const RatingsDisplay = ({
  className,
  rating,
  count,
  isLoading = false,
  useTransitions = false,
  t,
  layout = RatingsDisplayLayout.default,
}: Props) => {
  const withTransitions = React.useRef(useTransitions);
  // rating is undefined while loading (always in SSR) - skip label to avoid "Rated NaN"
  const a11yLabel = isLoading ? undefined : t('ratings-display.rating-aria-label', { rating });

  const getRatingInfo = () => {
    switch (layout) {
      case RatingsDisplayLayout.default:
        return `${rating.toFixed(1)} | ${t('ratings-display.count', {
          count,
        })}`;
      case RatingsDisplayLayout.bracket_separated:
        return `${rating.toFixed(1)} (${count})`;
      case RatingsDisplayLayout.count_only:
        return `(${count})`;
      case RatingsDisplayLayout.with_range:
        return `${rating.toFixed(1)} / 5 (${count})`;
      default:
        return;
    }
  };

  return (
    <span
      className={classNames(
        styles.container,
        { [styles.withTransitions]: withTransitions.current },
        styles[layout],
        className,
      )}
      data-hook={classNames('ratings-display', !isLoading ? 'is-ready' : '')}
    >
      {layout === RatingsDisplayLayout.with_range ? (
        <OneStar a11yLabel={a11yLabel} />
      ) : (
        <RatingStars rating={isLoading ? 0 : rating} a11yLabel={a11yLabel} />
      )}
      {!isLoading && (
        <span className={styles.ratingInfo}>
          {count === 0 ? t('ratings-display.no-ratings') : getRatingInfo()}
        </span>
      )}
    </span>
  );
};

export default withTranslate(RatingsDisplay);
